import React, { useEffect, useRef, useState } from "react";
import { API_BASE_URL } from '../../config';
import "./stylesresults.css";

function CallResults({ showCallStatuses, callResults, onUpdateCallResults }) {
  const [isUpdating, setIsUpdating] = useState(false);
  const [transcriptData, setTranscriptData] = useState(null);
  const [selectedCallId, setSelectedCallId] = useState(null);
  const [isSystemCollapsed, setIsSystemCollapsed] = useState(false);

  console.log('CallResults component rendered with:', {
    showCallStatuses,
    resultsCount: callResults?.length
  });

  // Enhanced status check function with better 500 error handling
  const checkCallStatus = async (callId) => {
    try {
      console.log('Checking status for call:', callId);

      // Guard clause for undefined callId
      if (!callId) {
        console.log('No call ID provided');
        return null;
      }

      const response = await fetch(`${API_BASE_URL}/check_call_status/${callId}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Raw status response:', data);

      // Handle successful call creation (201 status)
      if (data.status === 201 || data.status === "queued") {
        return {
          details: {
            id: callId,
            status: "queued",
            ...data.error_details // These aren't actually errors for 201
          },
          status: "queued"
        };
      }

      // Extract status from the correct location in the response
      const callStatus = data.error_details?.status ||
                        data.details?.status ||
                        data.status ||
                        "unknown";

      return {
        details: {
          id: callId,
          status: callStatus,
          recordingUrl: data.error_details?.recording_url ||
                       data.details?.recording_url,
          ...data.error_details
        },
        status: callStatus
      };

    } catch (error) {
      console.error('Error checking call status:', error);
      return null;
    }
  };

  // Updated effect to handle status updates
  useEffect(() => {
    let isMounted = true;

    const updateStatuses = async () => {
      if (!callResults?.length || !showCallStatuses || isUpdating) return;

      setIsUpdating(true);
      console.log('Starting status updates for calls:', callResults);

      try {
        const newResults = await Promise.all(
          callResults.map(async (result) => {
            // Extract callId from the correct location
            const callId = result?.call_result?.error_details?.id ||
                          result?.call_result?.details?.id;

            console.log('Processing call result:', {
              callId,
              currentStatus: result?.call_result?.status,
              details: result?.call_result?.error_details
            });

            if (!callId) return result;

            const statusData = await checkCallStatus(callId);
            if (!statusData) return result;

            // Create new result with the correct structure
            return {
              ...result,
              call_result: {
                details: statusData.details,
                status: statusData.status,
                error_details: statusData.details // Maintain backward compatibility
              }
            };
          })
        );

        if (isMounted) {
          console.log('Updated results:', newResults);
          onUpdateCallResults(newResults);
        }
      } catch (error) {
        console.error('Error updating statuses:', error);
      } finally {
        if (isMounted) {
          setIsUpdating(false);
        }
      }
    };

    updateStatuses();
    const intervalId = setInterval(updateStatuses, 5000);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [callResults, onUpdateCallResults, showCallStatuses, isUpdating]);

  // Update the handleTranscriptView function
  const handleTranscriptView = async (callId) => {
    try {
      setSelectedCallId(callId);
      const response = await fetch(`${API_BASE_URL}/get_call_transcript/${callId}`);
      const data = await response.json();

      // Filter out system messages from the transcript
      const filteredMessages = data.transcript?.messages?.filter(
        message => !message.text?.startsWith('system:') &&
                  !message.text?.startsWith('#Background') &&
                  !message.text?.includes('Your task:') &&
                  !message.text?.includes('Your goal:')
      ) || [];

      setTranscriptData({
        text: data.transcript?.text || "Transcript not yet available",
        // Use filtered messages instead of all messages
        messages: filteredMessages,
        summary: data.transcript?.summary || "Summary not yet available",
        audioUrl: data.audio?.url,
        metadata: {
          duration: data.metadata?.duration || 'N/A',
          status: data.metadata?.status || 'N/A',
          customer: data.metadata?.customer || {},
          createdAt: data.metadata?.created_at,
          endedAt: data.metadata?.ended_at
        }
      });
    } catch (error) {
      console.error('Error fetching transcript:', error);
      setTranscriptData({
        text: "Transcript processing or unavailable",
        messages: [],
        summary: "Please check back later",
        audioUrl: null,
        metadata: {
          status: 'processing',
          customer: {},
        }
      });
    }
  };

  // Early return if conditions aren't met
  if (!showCallStatuses || !callResults?.length) {
    console.log('No call results to display or results not visible');
    return null;
  }

  return (
    <div className="call-results">
      <h3>Call Results</h3>

      {isUpdating && (
        <div className="update-status">Updating call statuses...</div>
      )}

      <div className="call-results-list">
        {callResults.map((result, index) => {
          // Extract call details
          const callDetails = result?.call_result?.error_details ||
                             result?.call_result?.details || {};

          // Get candidate name from resume data
          const candidateName = result?.resume?.contactName?.trim() || 'Unknown Name';

          const callStatus = result?.call_result?.status ||
                            callDetails?.status ||
                            'N/A';

          // Get phone number from resume data
          const phoneNumber = result?.resume?.phoneNumbers?.[0] || 'N/A';

          // Check for recording URL in both possible locations
          const recordingUrl = callDetails?.recordingUrl || callDetails?.recording_url;

          console.log('Rendering call result:', {
            candidateName,
            callId: callDetails?.id,
            status: callStatus,
            phoneNumber,
            hasRecording: !!recordingUrl,
            recordingUrl,
            callDetails
          });

          return (
            <div key={`call-${callDetails?.id || index}`} className="call-result-item">
              <h4>{candidateName}</h4>
              <div className="call-details">
                <p>
                  <span className="label">Call ID:</span>
                  {callDetails?.id || 'N/A'}
                </p>
                <p>
                  <span className="label">Status:</span>
                  <span className={`status-${callStatus?.toLowerCase()}`}>
                    {callStatus}
                  </span>
                </p>
                <p>
                  <span className="label">Phone:</span>
                  {phoneNumber}
                </p>
                <p>
                  <span className="label">Created:</span>
                  {callDetails?.createdAt
                    ? new Date(callDetails.createdAt).toLocaleString()
                    : 'N/A'}
                </p>
                <p>
                  <span className="label">Last Updated:</span>
                  {callDetails?.updatedAt
                    ? new Date(callDetails.updatedAt).toLocaleString()
                    : 'N/A'}
                </p>
                <p>
                  <span className="label">Decision:</span>
                  <span className="decision-text">{result?.decision || 'N/A'}</span>
                </p>

                <button
                  onClick={() => handleTranscriptView(callDetails.id)}
                  className="view-transcript-btn"
                >
                  View Transcript
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Add Transcript Modal */}
      {transcriptData && selectedCallId && (
        <div className="transcript-modal">
          <div className="transcript-content">
            <div className="transcript-header">
              <h4>Call Transcript</h4>
              <button
                onClick={() => {
                  setTranscriptData(null);
                  setSelectedCallId(null);
                }}
                className="close-btn"
              >×</button>
            </div>

            {/* Call Details */}
            <div className="call-details-section">
              <div className="detail-item">
                <span>Status: {transcriptData.metadata?.status}</span>
              </div>
              <div className="detail-item">
                <span>Duration: {transcriptData.metadata?.duration}</span>
              </div>
            </div>

            {/* Audio Player */}
            {transcriptData.audioUrl && (
              <div className="audio-section">
                <h4>Recording</h4>
                <RecordingPlayer audioUrl={transcriptData.audioUrl} />
              </div>
            )}

            {/* Transcript Messages */}
            <div className="transcript-section">
              <h4>Transcript</h4>
              <div className="transcript-messages">
                {transcriptData.messages?.map((message, index) => {
                  if (message.role === 'system') {
                    return (
                      <div key={index} className="message system">
                        <div className="system-header">
                          <span>system:#BackgroundInformation</span>
                          <button
                            className="collapse-button"
                            onClick={() => setIsSystemCollapsed(!isSystemCollapsed)}
                          >
                            {isSystemCollapsed ? 'View More' : 'View Less'}
                          </button>
                        </div>
                        <div className={`system-content ${isSystemCollapsed ? 'collapsed' : ''}`}>
                          <span>{message.message}</span>
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div key={index} className={`message ${message.role}`}>
                      <strong>{message.role === 'assistant' ? 'ALICE AI:' : 'human:'} </strong>
                      <span>{message.message}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Summary Section */}
            {transcriptData.summary && (
              <div className="summary-section">
                <h4>Summary</h4>
                <p>{transcriptData.summary}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// RecordingPlayer component
const RecordingPlayer = ({ audioUrl }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState("00:00");
  const [duration, setDuration] = useState("00:00");

  useEffect(() => {
    const audio = audioRef.current;

    const updateProgress = () => {
      if (audio && !isNaN(audio.duration)) {
        const currentTime = audio.currentTime;
        const duration = audio.duration;
        const progress = (currentTime / duration) * 100;
        setProgress(progress);
        setCurrentTime(formatTime(currentTime));
        setDuration(formatTime(duration));
      }
    };

    const handleLoadedMetadata = () => {
      if (audio && !isNaN(audio.duration)) {
        setDuration(formatTime(audio.duration));
      }
    };

    const handleError = (error) => {
      console.error('Audio loading error:', error);
      setIsPlaying(false);
    };

    if (audio) {
      audio.addEventListener("timeupdate", updateProgress);
      audio.addEventListener("loadedmetadata", handleLoadedMetadata);
      audio.addEventListener("error", handleError);

      return () => {
        audio.removeEventListener("timeupdate", updateProgress);
        audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
        audio.removeEventListener("error", handleError);
      };
    }
  }, []);

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play().catch(error => {
          console.error('Playback failed:', error);
          setIsPlaying(false);
        });
      }
      setIsPlaying(!isPlaying);
    }
  };

  const formatTime = (timeInSeconds) => {
    if (isNaN(timeInSeconds)) return "00:00";
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes < 10 ? "0" : ""}${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  return (
    <div className="audio-player">
      <audio
        ref={audioRef}
        src={audioUrl}
        preload="metadata"
      />

      <div className="progress-bar">
        <div
          className="progress"
          style={{ width: `${progress}%` }}
        />
      </div>

      <div className="player-controls">
        <span className="time-display">{currentTime}</span>
        <button
          onClick={togglePlay}
          className="play-button"
          disabled={!audioUrl}
        >
          {isPlaying ? "Pause" : "Play"}
        </button>
        <span className="time-display">{duration}</span>
      </div>
    </div>
  );
};

export default CallResults;
